import React, { useEffect, useState, useRef } from "react";
import { api, mediaSrc, mediaSrcById, timeAgo, compressImage, uploadMedia } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toast } from "sonner";
import { Users, UsersRound, Briefcase, FileText, ChevronLeft, ChevronRight, X, ImageIcon, Camera, UserPlus, UserMinus, Mail, Phone, Copy } from "lucide-react";
import { AvatarCropDialog } from "./AvatarCropDialog";
import { AvatarLightbox } from "./AvatarLightbox";
import { resizeToJpeg } from "../lib/imageResize";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const LANG_LABEL = { en: "English", pt: "Português" };

/**
 * Info panel opened by clicking the conversation header name:
 * - DM: person photo/avatar centered, their info, media shared between the two of you
 * - Group/Crew: group avatar centered, members list, shared media
 * - Group creator can: change the group photo, add and remove members
 */
export function ConversationInfoDialog({ open, onClose, conversation, onChanged }) {
  const { user, setUser, t, lang } = useApp();
  const [media, setMedia] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index into images
  const [allUsers, setAllUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [avatarView, setAvatarView] = useState(null); // { src, name }
  const [cropFile, setCropFile] = useState(null); // picked file → circular crop dialog
  const avatarFileRef = useRef(null);
  // Per-conversation wallpaper (stored on the user profile, per conversation)
  const wpFileRef = useRef(null);
  const [wpBusy, setWpBusy] = useState(false);

  useEffect(() => {
    if (open && conversation) {
      setMedia(null);
      setAddOpen(false);
      api.get(`/conversations/${conversation.id}/media`).then((res) => setMedia(res.data)).catch(() => setMedia([]));
      if (conversation.type === "group") {
        api.get("/users").then((res) => setAllUsers(res.data));
      }
    }
  }, [open, conversation]);

  if (!conversation) return null;
  const isDM = conversation.type === "dm";
  const isGroup = conversation.type === "group";
  // Creator OR supervisor/office/manager can manage the group (members + photo)
  const isCreator = isGroup && conversation.created_by === user.id;
  const canManage = isGroup && (isCreator || ["supervisor", "office", "manager", "admin"].includes(user.role));
  const other = conversation.other_user;
  const members = conversation.participants || [];
  const memberIds = new Set(members.map((m) => m.id));
  const candidates = allUsers.filter((u) => !memberIds.has(u.id));

  // Copy the registered contact (email/phone) so it can be passed along
  const copyContact = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("contact_copied"));
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(t("contact_copied"));
      } catch (e2) {
        toast(value); // clipboard unavailable — at least surface the value
      }
    }
  };
  const images = (media || []).filter((m) => m.kind !== "document" && m.kind !== "audio");
  const docs = (media || []).filter((m) => m.kind === "document");

  const patchGroup = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/conversations/${conversation.id}/group`, { user_id: user.id, ...body });
      toast.success(t("group_updated"));
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const pickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCropFile(file);
  };

  const uploadCroppedGroupPhoto = async (b64) => {
    setCropFile(null);
    try {
      const m = await uploadMedia({ base64: b64, uploadedBy: user.id });
      await patchGroup({ avatar_media_id: m.id });
    } catch (err) {
      toast.error("Photo failed");
    }
  };

  const onPickConvWallpaper = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !conversation) return;
    setWpBusy(true);
    try {
      const b64 = await resizeToJpeg(file, 1440, 0.8);
      const up = await api.post("/media", {
        data_base64: b64, content_type: "image/jpeg", kind: "image",
        category: "wallpaper", uploaded_by: user.id, filename: "wallpaper.jpg",
      });
      await api.patch(`/users/${user.id}`, { conversation_wallpaper: { conversation_id: conversation.id, media_id: up.data.id } });
      setUser({ ...user, conv_wallpapers: { ...(user.conv_wallpapers || {}), [conversation.id]: up.data.id } });
      toast.success(t("wallpaper_updated"));
    } catch (err) {
      toast.error(t("something_wrong"));
    } finally {
      setWpBusy(false);
    }
  };

  const resetConvWallpaper = async () => {
    if (!conversation) return;
    setWpBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { conversation_wallpaper: { conversation_id: conversation.id, media_id: "" } });
      const next = { ...(user.conv_wallpapers || {}) };
      delete next[conversation.id];
      setUser({ ...user, conv_wallpapers: next });
      toast.success(t("wallpaper_reset"));
    } catch (err) {
      toast.error(t("something_wrong"));
    } finally {
      setWpBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="conversation-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{isDM ? t("contact_info") : t("group_info")}</DialogTitle>
          </DialogHeader>

          {/* Centered photo/avatar */}
          <div className="flex flex-col items-center text-center pt-1 pb-2">
            {isDM && other ? (
              <Avatar
                className={`h-24 w-24 mb-3 ${other.avatar_media_id ? "cursor-zoom-in active:scale-95 transition-transform" : ""}`}
                onClick={() => other.avatar_media_id && setAvatarView({ src: mediaSrcById(other.avatar_media_id), name: other.name })}
                data-testid="contact-avatar"
                role={other.avatar_media_id ? "button" : undefined}
              >
                {other.avatar_media_id && (
                  <AvatarImage src={mediaSrcById(other.avatar_media_id)} alt="" className="object-cover" />
                )}
                <AvatarFallback style={{ backgroundColor: other.avatar_color, color: "#fff" }} className="text-2xl font-semibold">
                  {initials(other.name)}
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="relative mb-3">
                {conversation.avatar_media_id ? (
                  <img
                    src={mediaSrcById(conversation.avatar_media_id)}
                    alt=""
                    data-testid="group-avatar-image"
                    role="button"
                    onClick={() => setAvatarView({ src: mediaSrcById(conversation.avatar_media_id), name: conversation.name })}
                    className="h-24 w-24 rounded-full object-cover border cursor-zoom-in active:scale-95 transition-transform"
                  />
                ) : (
                  <div className={`h-24 w-24 rounded-full flex items-center justify-center ${isGroup && conversation.job ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent-foreground"}`}>
                    {isGroup ? <UsersRound className="h-11 w-11" /> : <Users className="h-11 w-11" />}
                  </div>
                )}
                {canManage && (
                  <>
                    <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
                    <button
                      data-testid="group-photo-button"
                      onClick={() => avatarFileRef.current?.click()}
                      disabled={busy}
                      title={t("change_photo")}
                      className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:opacity-90"
                    >
                      <Camera className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="font-display font-semibold text-lg" data-testid="info-name">{conversation.name}</div>
            {isDM && other && (
              <div className="text-sm text-muted-foreground mt-0.5" data-testid="info-company-role">
                {other.company_name ? `${other.company_name} · ` : ""}
                {other.title}{t(other.role) !== other.title ? ` · ${t(other.role)}` : ""}
              </div>
            )}
            {!isDM && (
              <div className="text-sm text-muted-foreground mt-0.5">{members.length} {t("members")}</div>
            )}
            {isGroup && conversation.job && (
              <Badge variant="outline" className="mt-2 text-[10px] px-2 font-mono-plex text-primary border-primary/40" data-testid="info-job-badge">
                <Briefcase className="h-3 w-3 mr-1" /> #{conversation.job.job_number} — {conversation.job.customer_name}
              </Badge>
            )}
          </div>

          {/* Per-conversation wallpaper (personal — only affects your view) */}
          <div className="rounded-xl border p-3" data-testid="conv-wallpaper-row">
            <div className="flex items-center gap-3">
              <ImageIcon className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t("conv_wallpaper_title")}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.conv_wallpapers?.[conversation.id] ? t("wallpaper_custom") : t("wallpaper_default")}
                </div>
              </div>
              <div className="h-11 w-8 rounded-md border overflow-hidden shrink-0 bg-muted">
                <img
                  src={user?.conv_wallpapers?.[conversation.id]
                    ? mediaSrcById(user.conv_wallpapers[conversation.id])
                    : (user?.wallpaper_media_id ? mediaSrcById(user.wallpaper_media_id) : "/chat-bg.jpg")}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mt-2.5">
              <input ref={wpFileRef} type="file" accept="image/*" className="hidden" onChange={onPickConvWallpaper} />
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={wpBusy} onClick={() => wpFileRef.current?.click()} data-testid="conv-wallpaper-choose">
                {t("wallpaper_choose")}
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={wpBusy || !user?.conv_wallpapers?.[conversation.id]} onClick={resetConvWallpaper} data-testid="conv-wallpaper-reset">
                {t("wallpaper_default")}
              </Button>
            </div>
          </div>

          {/* DM: person details */}
          {isDM && other && (
            <div className="rounded-xl border divide-y" data-testid="info-person-details">
              {/* Registered contact — so an admin can pass it along to someone else */}
              {other.email && (
                <button
                  type="button"
                  onClick={() => copyContact(other.email)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-left hover:bg-muted/50 active:bg-muted transition-colors"
                  data-testid="info-contact-email"
                >
                  <span className="text-muted-foreground flex items-center gap-1.5 shrink-0">
                    <Mail className="h-3.5 w-3.5" /> {t("info_email")}
                  </span>
                  <span className="font-medium flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{other.email}</span>
                    <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
                  </span>
                </button>
              )}
              {other.phone && (
                <button
                  type="button"
                  onClick={() => copyContact(other.phone)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-left hover:bg-muted/50 active:bg-muted transition-colors"
                  data-testid="info-contact-phone"
                >
                  <span className="text-muted-foreground flex items-center gap-1.5 shrink-0">
                    <Phone className="h-3.5 w-3.5" /> {t("info_phone")}
                  </span>
                  <span className="font-medium flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{other.phone}</span>
                    <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
                  </span>
                </button>
              )}
              {other.crew_id && (
                <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">{t("crew")}</span>
                  <span className="font-medium">{other.crew_id.replace("crew-", "").toUpperCase()}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span className="text-muted-foreground">{t("language_label")}</span>
                <span className="font-medium">{LANG_LABEL[other.language] || other.language || "—"}</span>
              </div>
            </div>
          )}

          {/* Group/Crew: members */}
          {!isDM && members.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-1 pb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t("members")}</span>
                {canManage && (
                  <button
                    data-testid="add-member-button"
                    onClick={() => setAddOpen((v) => !v)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                  >
                    <UserPlus className="h-3.5 w-3.5" /> {t("add_member")}
                  </button>
                )}
              </div>
              {canManage && addOpen && (
                <div className="rounded-xl border divide-y mb-2 max-h-44 overflow-y-auto" data-testid="add-member-list">
                  {candidates.length === 0 && (
                    <div className="text-center text-xs text-muted-foreground py-3">—</div>
                  )}
                  {candidates.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`add-member-row-${c.id}`}
                      disabled={busy}
                      onClick={() => patchGroup({ add_ids: [c.id] })}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/60 transition-colors text-left"
                    >
                      <Avatar className="h-8 w-8">
                        {c.avatar_media_id && (
                          <AvatarImage src={mediaSrcById(c.avatar_media_id)} alt="" className="object-cover" />
                        )}
                        <AvatarFallback style={{ backgroundColor: c.avatar_color, color: "#fff" }} className="text-[10px] font-semibold">
                          {initials(c.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                      <UserPlus className="h-4 w-4 text-primary shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              <div className="rounded-xl border divide-y" data-testid="info-members-list">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`info-member-row-${m.id}`}>
                    <Avatar className="h-9 w-9">
                      {m.avatar_media_id && (
                        <AvatarImage src={mediaSrcById(m.avatar_media_id)} alt="" className="object-cover" />
                      )}
                      <AvatarFallback style={{ backgroundColor: m.avatar_color, color: "#fff" }} className="text-[11px] font-semibold">
                        {initials(m.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.title}</div>
                    </div>
                    <Badge variant="secondary" className="text-[10px] shrink-0">{t(m.role)}</Badge>
                    {canManage && m.id !== conversation.created_by && (
                      <button
                        data-testid={`remove-member-${m.id}`}
                        disabled={busy}
                        onClick={() => patchGroup({ remove_ids: [m.id] })}
                        title={t("remove_member")}
                        className="h-7 w-7 rounded-full flex items-center justify-center text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shared media */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pb-1.5 flex items-center justify-between">
              <span>{t("shared_media")}</span>
              {media && <span>{images.length + docs.length}</span>}
            </div>
            {!media && (
              <div className="grid grid-cols-4 gap-1.5">
                {[1, 2, 3, 4].map((i) => <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />)}
              </div>
            )}
            {media && images.length === 0 && docs.length === 0 && (
              <div className="text-center text-xs text-muted-foreground py-6 border rounded-xl">
                <ImageIcon className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
                {t("no_media")}
              </div>
            )}
            {media && images.length > 0 && (
              <div className="grid grid-cols-4 gap-1.5" data-testid="info-media-grid">
                {images.map((m, i) => (
                  <button
                    key={m.id}
                    data-testid={`info-media-thumb-${m.id}`}
                    onClick={() => setLightbox(i)}
                    className="aspect-square rounded-lg overflow-hidden border hover:opacity-80 transition-opacity"
                  >
                    <img src={mediaSrc(m)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            {media && docs.length > 0 && (
              <div className="mt-2 space-y-1">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="truncate flex-1">{d.filename || "document"}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(d.created_at, lang)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Media lightbox with prev/next */}
      {lightbox !== null && images[lightbox] && (
        <Dialog open onOpenChange={(o) => !o && setLightbox(null)}>
          <DialogContent className="max-w-4xl p-0 bg-black/95 border-none" data-testid="info-media-lightbox">
            <div className="relative flex items-center justify-center min-h-[50vh] max-h-[85vh] p-2">
              <button
                data-testid="lightbox-close"
                onClick={() => setLightbox(null)}
                className="absolute top-2 right-2 z-20 h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
              {lightbox > 0 && (
                <button
                  data-testid="lightbox-prev"
                  onClick={() => setLightbox(lightbox - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}
              {lightbox < images.length - 1 && (
                <button
                  data-testid="lightbox-next"
                  onClick={() => setLightbox(lightbox + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              )}
              <img src={mediaSrc(images[lightbox])} alt="" className="max-w-full max-h-[80vh] object-contain rounded" />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Circular crop for the group photo */}
      <AvatarCropDialog
        open={!!cropFile}
        file={cropFile}
        onClose={() => setCropFile(null)}
        onCropped={uploadCroppedGroupPhoto}
      />
      <AvatarLightbox src={avatarView?.src} name={avatarView?.name} onClose={() => setAvatarView(null)} />
    </>
  );
}
